/// <reference types="@types/google.maps" />
import { useRef, useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2, AlertCircle } from "lucide-react";

interface AddressData {
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
  formattedAddress?: string;
  latitude?: string;
  longitude?: string;
  placeId?: string;
  isVerified?: number;
}

interface AddressInputProps {
  value?: string;
  onAddressSelect: (address: AddressData) => void;
  onApiError?: () => void; // NEW: Callback when Google API fails - parent should switch to manual entry
  placeholder?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

interface WindowWithGoogle extends Window {
  google?: {
    maps?: {
      places?: {
        Autocomplete: new (
          input: HTMLInputElement,
          options?: google.maps.places.AutocompleteOptions
        ) => google.maps.places.Autocomplete;
      };
    };
  };
  initGooglePlaces?: () => void;
  gm_authFailure?: () => void; // Google Maps auth failure callback
}

// ISSUE-054 Rework #3: Helper to clean up floating .pac-container elements
const cleanupPacContainers = () => {
  // Remove all .pac-container elements from the DOM
  const pacContainers = document.querySelectorAll('.pac-container');
  pacContainers.forEach(container => {
    container.remove();
  });
};

// ISSUE-054 Rework #3: Export cleanup function for parent components (modals) to use
export { cleanupPacContainers };

export function AddressInput({
  value = "",
  onAddressSelect,
  onApiError,
  placeholder = "Search address...",
  disabled = false,
  "data-testid": testId,
}: AddressInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [inputValue, setInputValue] = useState(value);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasApiError, setHasApiError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Handle Google Maps API authentication failure
  const handleAuthFailure = useCallback(() => {
    console.error("Google Maps API authentication failed. Check API key configuration.");
    setHasApiError(true);
    setIsLoading(false);
    setIsLoaded(false);
    setErrorMessage("Google Maps API configuration error. Using manual entry.");
    if (onApiError) {
      onApiError();
    }
  }, [onApiError]);

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
      setIsLoading(false);
      console.warn("Google Maps API key not configured. Address autocomplete disabled.");
      return;
    }

    // Set up global auth failure handler BEFORE loading script
    const win = window as WindowWithGoogle;
    win.gm_authFailure = handleAuthFailure;

    const loadGoogleMaps = () => {
      if (win.google?.maps?.places) {
        setIsLoaded(true);
        setIsLoading(false);
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        const existingCallback = win.initGooglePlaces;
        win.initGooglePlaces = () => {
          if (existingCallback) existingCallback();
          setIsLoaded(true);
          setIsLoading(false);
        };
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGooglePlaces`;
      script.async = true;
      script.defer = true;

      // Handle script loading errors
      script.onerror = () => {
        console.error("Failed to load Google Maps API script");
        setHasApiError(true);
        setIsLoading(false);
        setErrorMessage("Failed to load Google Maps. Using manual entry.");
        if (onApiError) {
          onApiError();
        }
      };

      win.initGooglePlaces = () => {
        // Additional check: verify API actually loaded correctly
        if (!win.google?.maps?.places) {
          console.error("Google Maps Places API not available after script load");
          setHasApiError(true);
          setIsLoading(false);
          setErrorMessage("Google Maps Places API unavailable. Using manual entry.");
          if (onApiError) {
            onApiError();
          }
          return;
        }
        setIsLoaded(true);
        setIsLoading(false);
      };

      document.head.appendChild(script);
    };

    // Timeout fallback: if Google Maps doesn't load in 10 seconds, switch to manual
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.warn("Google Maps API load timeout. Switching to manual entry.");
        setHasApiError(true);
        setIsLoading(false);
        setErrorMessage("Google Maps timed out. Using manual entry.");
        if (onApiError) {
          onApiError();
        }
      }
    }, 10000);

    loadGoogleMaps();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [handleAuthFailure, onApiError, isLoading]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current || autocompleteRef.current) return;

    const win = window as WindowWithGoogle;

    try {
      if (!win.google?.maps?.places) return;

      autocompleteRef.current = new win.google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: { country: "au" },
          fields: ["address_components", "geometry", "formatted_address", "place_id"],
          types: ["address"],
        }
      );

      autocompleteRef.current.addListener("place_changed", () => {
        console.log("[AddressInput] place_changed event fired");
        const place = autocompleteRef.current?.getPlace();
        console.log("[AddressInput] Place result:", place);
        if (!place?.address_components) {
          console.warn("[AddressInput] No address_components in place result");
          return;
        }

        const addressData = parseAddressComponents(place);
        console.log("[AddressInput] Parsed address data:", addressData);
        setInputValue(addressData.formattedAddress || "");
        onAddressSelect(addressData);

        // ISSUE-054 Rework #3: Clean up dropdown after selection
        // Small delay to ensure the selection is processed before cleanup
        setTimeout(() => {
          cleanupPacContainers();
        }, 100);
      });
    } catch (error) {
      console.error("Error initializing Google Places Autocomplete:", error);
    }

    // ISSUE-054 Rework #3: Cleanup on unmount to prevent floating dropdowns
    return () => {
      cleanupPacContainers();
      autocompleteRef.current = null;
    };
  }, [isLoaded, onAddressSelect]);

  const parseAddressComponents = (
    place: google.maps.places.PlaceResult
  ): AddressData => {
    const components = place.address_components || [];
    
    const getComponent = (type: string): string => {
      const component = components.find((c: google.maps.GeocoderAddressComponent) => c.types.includes(type));
      return component?.long_name || "";
    };

    const getShortComponent = (type: string): string => {
      const component = components.find((c: google.maps.GeocoderAddressComponent) => c.types.includes(type));
      return component?.short_name || "";
    };

    let unitNumber: string | undefined;
    let streetNumber = getComponent("street_number");
    
    if (streetNumber.includes("/")) {
      const parts = streetNumber.split("/");
      unitNumber = parts[0];
      streetNumber = parts[1];
    }

    const subpremise = getComponent("subpremise");
    if (subpremise && !unitNumber) {
      unitNumber = subpremise;
    }

    return {
      unitNumber: unitNumber || undefined,
      streetNumber: streetNumber || undefined,
      streetName: getComponent("route") || undefined,
      suburb: getComponent("locality") || getComponent("sublocality_level_1") || undefined,
      state: getShortComponent("administrative_area_level_1") || undefined,
      postcode: getComponent("postal_code") || undefined,
      country: getComponent("country") || "Australia",
      formattedAddress: place.formatted_address || undefined,
      latitude: place.geometry?.location?.lat()?.toString() || undefined,
      longitude: place.geometry?.location?.lng()?.toString() || undefined,
      placeId: place.place_id || undefined,
      isVerified: 1,
    };
  };

  // If there's an API error, don't render the component (parent should show manual entry)
  if (hasApiError && errorMessage) {
    return (
      <div className="relative">
        <div className="flex items-center gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
          <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            {errorMessage}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <MapPin className="h-4 w-4" />
        )}
      </div>
      <Input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        className="pl-9"
        data-testid={testId}
      />
      {!isLoaded && !isLoading && !hasApiError && (
        <p className="text-xs text-muted-foreground mt-1">
          Address autocomplete unavailable. Enter address manually.
        </p>
      )}
    </div>
  );
}
